// Warehouse dashboard — landing page with a snapshot of the whole module.
//
// Everything here is derived client-side from the same hooks the other pages
// use (useOccupancy, useItems, useTrips, useMovements). No dedicated
// /dashboard endpoint to maintain, no chart library dependency — just
// react-bootstrap cards and flex bars. When the UI needs a real chart later
// (trend over time, cable family mix, etc.) we can add recharts then.

import React, { useMemo } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Button,
  Spinner,
  Alert,
  Table,
  ProgressBar,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import { useBuildings } from '../../features/warehouse/useBuildings.js';
import { useOccupancy } from '../../features/warehouse/useOccupancy.js';
import { useItems } from '../../features/warehouse/useItems.js';
import { useTrips } from '../../features/warehouse/useTrips.js';
import { useMovements } from '../../features/warehouse/useMovements.js';
import {
  movementActionColor,
  movementActionLabel,
  typeBadgeColor,
  tripStatusColor,
} from '../../features/warehouse/warehouseUtils.js';
import WarehouseStockTable from '../../components/warehouse/WarehouseStockTable.js';
import WarehouseTasks from '../../components/warehouse/WarehouseTasks.js';

export default function DashboardPage({ onNavigate: onNavigateProp }) {
  const navigate = useNavigate();
  const onNavigate = onNavigateProp || ((target) => {
    if (target === 'map') navigate('/apps/warehouse-management/map');
    else if (target === 'trips') navigate('/apps/warehouse-management/trips');
    else if (target === 'kpis') navigate('/apps/warehouse-management/kpis');
  });
  const { data: buildings, isLoading: loadingBuildings } = useBuildings();
  const buildingId = buildings?.[0]?._id || null;

  const { data: occupancyData } = useOccupancy(buildingId);
  const { data: items } = useItems();
  const { data: trips } = useTrips();
  const { data: movements } = useMovements({ limit: 15 });

  const slotStats = useMemo(() => computeSlotStats(occupancyData), [occupancyData]);
  const aisleStats = useMemo(() => computeAisleStats(occupancyData), [occupancyData]);
  const itemStats = useMemo(() => computeItemStats(items), [items]);
  const tripStats = useMemo(() => computeTripStats(trips), [trips]);

  if (loadingBuildings) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  if (!buildings?.length) {
    return (
      <Container className="py-5">
        <Alert variant="warning">
          No buildings found. Run <code>npm run seed</code> in the warehouse-module backend.
        </Alert>
      </Container>
    );
  }

  const building = occupancyData?.building;

  return (
    <Container fluid className="py-3">
      <Row className="mb-3 align-items-center g-2">
        <Col>
          <h4 className="mb-0">Warehouse dashboard</h4>
          {building && (
            <div className="text-muted small">
              {building.code} — {building.name}
            </div>
          )}
        </Col>
        <Col xs="auto">
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate('/apps/warehouse-management/add-stock')}
          >
            + Add / sync stock
          </Button>
        </Col>
      </Row>

      {/* Top row: the four headline KPIs */}
      <Row className="g-3 mb-3">
        <Col md={3}>
          <StatCard
            label="Slot occupancy"
            primary={slotStats ? `${slotStats.occupiedPct}%` : '—'}
            secondary={
              slotStats
                ? `${slotStats.occupied + slotStats.reserved}/${slotStats.total} slots`
                : null
            }
            footer={
              slotStats && (
                <ProgressBar style={{ height: 8 }}>
                  <ProgressBar variant="success" now={slotStats.occupiedPct} />
                  <ProgressBar variant="primary" now={slotStats.reservedPct} />
                  <ProgressBar variant="danger" now={slotStats.blockedPct} />
                </ProgressBar>
              )
            }
            onClick={() => onNavigate?.('map')}
          />
        </Col>
        <Col md={3}>
          <StatCard
            label="Items waiting put-away"
            primary={itemStats ? itemStats.waiting : '—'}
            secondary={itemStats ? `${itemStats.placed} placed` : null}
            onClick={() => onNavigate?.('map')}
          />
        </Col>
        <Col md={3}>
          <StatCard
            label="Active trips"
            primary={tripStats ? tripStats.active : '—'}
            secondary={tripStats ? `${tripStats.shipped} shipped all-time` : null}
            onClick={() => onNavigate?.('trips')}
          />
        </Col>
        <Col md={3}>
          <StatCard
            label="Stock on hand"
            primary={itemStats ? `${(itemStats.totalLengthM / 1000).toFixed(1)} km` : '—'}
            secondary={itemStats ? `${(itemStats.totalWeightKg / 1000).toFixed(1)} t` : null}
          />
        </Col>
      </Row>

      <Row className="g-3">
        {/* Aisle breakdown */}
        <Col md={5}>
          <Card className="h-100">
            <Card.Header><strong>Aisle occupancy</strong></Card.Header>
            <Card.Body>
              {!aisleStats && <Spinner animation="border" size="sm" />}
              {aisleStats && aisleStats.length === 0 && (
                <div className="text-muted small">No slots in this building.</div>
              )}
              {aisleStats?.map(a => (
                <div key={a.aisle} className="mb-2">
                  <div className="d-flex justify-content-between small">
                    <span className="fw-bold">Aisle {a.aisle}</span>
                    <span className="text-muted">
                      {a.occupied + a.reserved}/{a.total}
                    </span>
                  </div>
                  <ProgressBar style={{ height: 10 }}>
                    <ProgressBar variant="success" now={(a.occupied / a.total) * 100} />
                    <ProgressBar variant="primary" now={(a.reserved / a.total) * 100} />
                    <ProgressBar variant="danger" now={(a.blocked / a.total) * 100} />
                  </ProgressBar>
                </div>
              ))}
            </Card.Body>
          </Card>
        </Col>

        {/* Stock mix by type */}
        <Col md={3}>
          <Card className="h-100">
            <Card.Header><strong>Stock by type</strong></Card.Header>
            <Card.Body>
              {!itemStats && <Spinner animation="border" size="sm" />}
              {itemStats && (
                <Table size="sm" className="mb-0">
                  <tbody>
                    {['drum', 'europallet', 'coil'].map(type => (
                      <tr key={type}>
                        <td>
                          <Badge bg={typeBadgeColor(type)}>{type}</Badge>
                        </td>
                        <td className="text-end">
                          <strong>{itemStats.byType[type] || 0}</strong>
                          <div className="small text-muted">
                            {((itemStats.lengthByType[type] || 0) / 1000).toFixed(1)} km
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Recent movements feed */}
        <Col md={4}>
          <Card className="h-100">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>Recent activity</strong>
              <Badge bg="secondary">{movements?.length || 0}</Badge>
            </Card.Header>
            <Card.Body className="p-0" style={{ maxHeight: 360, overflowY: 'auto' }}>
              {!movements && (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                </div>
              )}
              {movements?.length === 0 && (
                <div className="text-muted small text-center py-3">
                  No movements yet.
                </div>
              )}
              {movements?.length > 0 && (
                <Table size="sm" className="mb-0">
                  <tbody>
                    {movements.map(m => (
                      <tr key={m._id}>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <Badge bg={movementActionColor(m.action)}>
                            {movementActionLabel(m.action)}
                          </Badge>
                        </td>
                        <td className="small">
                          <div><strong>{m.itemSerial}</strong></div>
                          <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                            {new Date(m.at).toLocaleString()}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Trip status strip */}
      {tripStats && tripStats.byStatus.length > 0 && (
        <Row className="mt-3">
          <Col>
            <Card>
              <Card.Header><strong>Trips by status</strong></Card.Header>
              <Card.Body className="d-flex flex-wrap gap-3">
                {tripStats.byStatus.map(([status, count]) => (
                  <div key={status} className="text-center">
                    <div className="h4 mb-0">{count}</div>
                    <Badge bg={tripStatusColor(status)} className="text-capitalize">
                      {status}
                    </Badge>
                  </div>
                ))}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      <Row className="mt-4">
        <Col>
          <WarehouseTasks title="Warehouse Tasks — gestionar" />
        </Col>
      </Row>

      <Row className="mt-4">
        <Col>
          <WarehouseStockTable title="Warehouse Stock" />
        </Col>
      </Row>
    </Container>
  );
}

// ---------------------------------------------------------------------------

function StatCard({ label, primary, secondary, footer, onClick }) {
  return (
    <Card
      className="h-100"
      style={onClick ? { cursor: 'pointer' } : undefined}
      onClick={onClick}
    >
      <Card.Body>
        <div className="text-muted small text-uppercase">{label}</div>
        <div className="h3 mb-0 mt-1">{primary}</div>
        {secondary && <div className="small text-muted">{secondary}</div>}
        {footer && <div className="mt-2">{footer}</div>}
      </Card.Body>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Pure derivation helpers — kept out of the component so the JSX stays lean.
// ---------------------------------------------------------------------------

function computeSlotStats(occupancyData) {
  if (!occupancyData?.occupancy) return null;
  const counts = { empty: 0, occupied: 0, reserved: 0, blocked: 0, total: 0 };
  for (const entry of Object.values(occupancyData.occupancy)) {
    counts.total++;
    if (entry.status === 'blocked') counts.blocked++;
    else if (entry.status === 'empty') counts.empty++;
    else if (entry.item?.status === 'reserved') counts.reserved++;
    else counts.occupied++;
  }
  const pct = (n) => (counts.total ? Math.round((n / counts.total) * 100) : 0);
  return {
    ...counts,
    occupiedPct: pct(counts.occupied),
    reservedPct: pct(counts.reserved),
    blockedPct: pct(counts.blocked),
  };
}

function computeAisleStats(occupancyData) {
  if (!occupancyData?.occupancy) return null;
  const byAisle = new Map();
  for (const entry of Object.values(occupancyData.occupancy)) {
    const key = entry.aisle || '?';
    if (!byAisle.has(key)) {
      byAisle.set(key, { aisle: key, empty: 0, occupied: 0, reserved: 0, blocked: 0, total: 0 });
    }
    const a = byAisle.get(key);
    a.total++;
    if (entry.status === 'blocked') a.blocked++;
    else if (entry.status === 'empty') a.empty++;
    else if (entry.item?.status === 'reserved') a.reserved++;
    else a.occupied++;
  }
  return [...byAisle.values()].sort((a, b) => a.aisle.localeCompare(b.aisle));
}

function computeItemStats(items) {
  if (!items) return null;
  const stats = {
    waiting: 0,
    placed: 0,
    totalLengthM: 0,
    totalWeightKg: 0,
    byType: {},
    lengthByType: {},
  };
  for (const item of items) {
    // Skip terminal states from stock-on-hand totals.
    if (item.status === 'shipped' || item.status === 'scrapped' || item.status === 'loaded') {
      continue;
    }
    stats.totalLengthM += item.lengthMeters || 0;
    stats.totalWeightKg += item.weightKg || 0;
    stats.byType[item.type] = (stats.byType[item.type] || 0) + 1;
    stats.lengthByType[item.type] = (stats.lengthByType[item.type] || 0) + (item.lengthMeters || 0);
    if (item.location?.kind === 'slot') stats.placed++;
    else stats.waiting++;
  }
  return stats;
}

function computeTripStats(trips) {
  if (!trips) return null;
  const byStatusMap = new Map();
  let active = 0;
  let shipped = 0;
  for (const trip of trips) {
    byStatusMap.set(trip.status, (byStatusMap.get(trip.status) || 0) + 1);
    if (trip.status === 'draft' || trip.status === 'loading' || trip.status === 'loaded') {
      active++;
    }
    if (trip.status === 'shipped' || trip.status === 'invoiced') shipped++;
  }
  return {
    active,
    shipped,
    byStatus: [...byStatusMap.entries()].sort((a, b) => a[0].localeCompare(b[0])),
  };
}
